import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Typography, Box, Tabs, Tab, CardContent } from "@mui/material";
import { Save, Delete, ArrowBack } from "@mui/icons-material";
import { MuiColorInput } from "mui-color-input";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import ToggleSwitch from "../backend/ToggleSwitch";
import WayfinderButtonSettings from "./WayfinderButtonSettings";
import MainWayfinderMedia from "./MainWayfinderMedia";
import WayfinderLocationSettings from "./WayfinderLocationSettings";
import WayfinderAvatarSettings from "./WayfinderAvatarSettings";
import GlobalAvatarSettings from "./GlobalAvatarSettings";
import LanguageSelector from "./LanguageSelector";
import LocationVideoProgress from "./LocationVideoProgress";
import TextDirectionManager from "./TextDirectionManager";
import LocationQRCodeGenerator from "./LocationQRCodeGenerator";
import MapPositionSettings from "./MapPositionSettings";
import MobileScreenTextEditor from "./MobileScreenTextEditor";
import { getLanguageText } from "../../utils/multilanguageUtils";
import StepByStepDirectionsConfig from "./StepByStepDirectionsConfig";

function WayfinderLocationTemplate({
  type,
  buttonColor = "#000000",
  userId,
  handleDelete,
  handleReset,
  resolvedButtonSettings,
  pngFile,
  setPngFile,
  clickSoundFile,
  setClickSoundFile,
  handleUploadClickSound,
  handleUploadPng,
  btnIcon,
  setBtnIcon,
  handleUploadIcon,

  item,
  setItem,
  handleSave,
  handleSaveButtonText,
  setPhotoFile,
  setMediaLogoFile,
  setAvatarVideoFile,
  setMapVideoFile,
  setQrCodeFile,
  setUpperLeftLogoFile,
  setUpperRightLogoFile,
  setBottomLeftLogoFile,
  setBottomRightLogoFile,
  uploadingStates,
  setUploadingStates,
  handleUploadMedia,
  handleDeleteMedia,
  loadingMedia,
  fetchLocationData,
  locationId,
  // Language and video progress props
  currentLanguage,
  onLanguageChange,
  // Text direction update props
  onTextDirectionsUpdate,
  // Map video props
  mapVideoFile,
  handleUploadMapVideo,
  // Avatar video props
  avatarVideoFile,
  handleUploadAvatarVideo,
  // Logo before map props
  logoBeforeMapFile,
  setLogoBeforeMapFile,
  handleUploadLogoBeforeMap,
  setCurrentLanguage,
  // Step-by-Step Directions props
  stepByStepConfig,
  setStepByStepConfig,
  saveStepByStepConfig,
  parentOptions = [],
}) {
  console.log("logoBeforeMapFile", logoBeforeMapFile);

  const [activeTab, setActiveTab] = useState(0);
  const [videoStatus, setVideoStatus] = useState(null);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [loadingLanguages, setLoadingLanguages] = useState(false);


  const navigate = useNavigate();

  // Language helper functions
  const getLanguageFlag = (languageCode) => {
    const flagMap = {
      'en': 'us',
      'es': 'es',
      'fr': 'fr',
      'de': 'de',
      'it': 'it',
      'pt': 'pt',
      'ru': 'ru',
      'ja': 'jp',
      'ko': 'kr',
      'zh': 'cn',
      'ar': 'sa',
      'hi': 'in',
    };
    return flagMap[languageCode] || 'us';
  };

  const getLanguageName = (languageCode) => {
    const nameMap = {
      'en': 'English',
      'es': 'Spanish',
      'fr': 'French',
      'de': 'German',
      'it': 'Italian',
      'pt': 'Portuguese',
      'ru': 'Russian',
      'ja': 'Japanese',
      'ko': 'Korean',
      'zh': 'Chinese',
      'ar': 'Arabic',
      'hi': 'Hindi',
    };
    return nameMap[languageCode] || languageCode.toUpperCase();
  };

  const handleLanguageChange = async (newLanguage) => {
    try {
      // Update current language state
      setCurrentLanguage(newLanguage);

      // Save preference to localStorage
      localStorage.setItem(`language_pref_${userId}`, newLanguage);

      // Call parent's language change handler if provided
      if (onLanguageChange) {
        onLanguageChange(newLanguage);
      }

      // Reload location data for the new language
      if (locationId && fetchLocationData) {
        await fetchLocationData(locationId);
      }

      // Show success message (you can add toast notification here if available)
      console.log(`Language changed to ${getLanguageName(newLanguage)}`);
    } catch (error) {
      console.error('Error changing language:', error);
    }
  };

  // Fetch available languages
  useEffect(() => {
    const fetchAvailableLanguages = async () => {
      try {
        setLoadingLanguages(true);
        const response = await axios.get(`/api/language/${userId}`);
        if (response.data.success) {
          setAvailableLanguages(response.data.languages || ["en"]);

          // Try to get saved language preference from localStorage
          const savedLang = localStorage.getItem(`language_pref_${userId}`);
          if (savedLang && response.data.languages.includes(savedLang)) {
            // Language is already set via props, just update available languages
          } else {
            // Default to first language or 'en' if no saved preference
            const defaultLang = response.data.languages[0] || "en";
            if (!currentLanguage || currentLanguage === "en") {
              // Only update if currentLanguage is not already set
              if (onLanguageChange) {
                onLanguageChange(defaultLang);
              }
            }
          }
        }
      } catch (error) {
        console.error("Error fetching languages:", error);
        setAvailableLanguages(["en"]);
      } finally {
        setLoadingLanguages(false);
      }
    };

    if (userId) {
      fetchAvailableLanguages();
    }
  }, [userId]);

  const handleToggleChangeMedia = (mediaKey, on) => {
    setItem((prev) => ({
      ...prev,
      uploadedUrls: {
        ...((prev && prev.uploadedUrls) || {}),
        [mediaKey]: {
          ...(((prev && prev.uploadedUrls) || {})[mediaKey] || {}),
          enabled: on,
        },
      },
    }));
  };

  console.log("Rendering WayfinderLocationTemplate with item:", item);

  return (
    <div className="main-container">
      <div className="pd-ltr-20 xs-pd-20-10 dashboard-container">
        <Box sx={{ mx: "auto" }}>
          {/* Header */}
          <div className="page-header">
            <div className="row">
              <div className="col-md-6 col-sm-12">
                <div className="title">
                  <Typography variant="h4">
                    Wayfinder {">>"}{" "}
                    <span>
                      {type === "location" ? "Location" : "Sub Location"}
                    </span>
                    {">>"}{" "}
                    <span style={{ color: "blue" }}>
                      {getLanguageText(item.buttonText, currentLanguage)?.replace(/<br\s*\/?>/gi, "") ||
                        (type === "location" ? "Location" : "Sub Location")}
                    </span>
                  </Typography>
                </div>
              </div>
              <div className="col-md-3 col-sm-12 d-flex align-items-center justify-content-end">
                {loadingLanguages ? (
                  <div className="d-flex align-items-center">
                    <div className="spinner-border spinner-border-sm me-2" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <span>Loading languages...</span>
                  </div>
                ) : availableLanguages.length > 1 ? (
                  <div className="language-switcher">
                    <div className="dropdown">
                      <button
                        className="btn btn-outline-secondary dropdown-toggle d-flex align-items-center"
                        type="button"
                        id="languageDropdown"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        style={{
                          borderColor: buttonColor,
                          color: buttonColor,
                          backgroundColor: "transparent",
                        }}
                      >
                        <span
                          className={`fi fi-${getLanguageFlag(currentLanguage)} me-2`}
                          style={{ fontSize: "16px" }}
                        ></span>
                        <span className="me-1">{getLanguageName(currentLanguage)}</span>
                        <span
                          className="badge bg-primary ms-1"
                          style={{ backgroundColor: buttonColor }}
                        >
                          {availableLanguages.length}
                        </span>
                      </button>
                      <ul className="dropdown-menu" aria-labelledby="languageDropdown">
                        {availableLanguages.map((lang) => (
                          <li key={lang}>
                            <button
                              className={`dropdown-item d-flex align-items-center ${currentLanguage === lang ? "active" : ""
                                }`}
                              onClick={() => handleLanguageChange(lang)}
                              style={{
                                backgroundColor:
                                  currentLanguage === lang ? `${buttonColor}20` : "transparent",
                              }}
                            >
                              <span
                                className={`fi fi-${getLanguageFlag(lang)} me-2`}
                                style={{ fontSize: "16px" }}
                              ></span>
                              <span>{getLanguageName(lang)}</span>
                              {currentLanguage === lang && (
                                <i className="fas fa-check ms-auto text-success"></i>
                              )}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ) : (
                  <div className="d-flex align-items-center text-muted">
                    <span
                      className={`fi fi-${getLanguageFlag(currentLanguage)} me-2`}
                      style={{ fontSize: "16px" }}
                    ></span>
                    <span>{getLanguageName(currentLanguage)}</span>
                    <small className="ms-2">(Only language available)</small>
                  </div>
                )}
              </div>
              <div className="col-md-3 col-sm-12" style={{ width: "100%" }}>
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  style={{ float: "right", backgroundColor: buttonColor }}
                  className="btn btn-success"
                >
                  <ArrowBack style={{ marginRight: "5px" }} />
                  Go Back
                </button>
              </div>
            </div>
          </div>

          {/* Tabs Navigation */}
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              "& .MuiTab-root": { minWidth: 100 },
              "& .Mui-selected": { fontWeight: "bold" },
            }}
          >
            <Tab
              sx={{
                "&:hover": {
                  backgroundColor: buttonColor,
                  color: "#fff",
                  fontWeight: "bold",
                },
              }}
              label="Button Settings"
            />

            <Tab
              sx={{
                "&:hover": {
                  backgroundColor: buttonColor,
                  color: "#fff",
                  fontWeight: "bold",
                },
              }}
              label="Location Screen Content"
            />

            <Tab
              sx={{
                "&:hover": {
                  backgroundColor: buttonColor,
                  color: "#fff",
                  fontWeight: "bold",
                },
              }}
              label="Location Settings"
            />

            <Tab
              sx={{
                "&:hover": {
                  backgroundColor: buttonColor,
                  color: "#fff",
                  fontWeight: "bold",
                },
              }}
              label="Avatar Settings"
            />

            <Tab
              sx={{
                "&:hover": {
                  backgroundColor: buttonColor,
                  color: "#fff",
                  fontWeight: "bold",
                },
              }}
              label="Avatar API Settings"
            />

            <Tab
              sx={{
                "&:hover": {
                  backgroundColor: buttonColor,
                  color: "#fff",
                  fontWeight: "bold",
                },
              }}
              label="Mobile Screen Text"
            />

            <Tab
              sx={{
                "&:hover": {
                  backgroundColor: buttonColor,
                  color: "#fff",
                  fontWeight: "bold",
                },
              }}
              label="Step-by-Step Directions"
            />

            {/* <Tab
              sx={{
                "&:hover": {
                  backgroundColor: buttonColor,
                  color: "#fff",
                  fontWeight: "bold",
                },
              }}
              label="Map Position"
            /> */}
          </Tabs>

          {/* Tab Content */}

          {activeTab === 0 && (
            <WayfinderButtonSettings
              handleDelete={handleDelete}
              handleReset={handleReset}
              resolvedButtonSettings={resolvedButtonSettings}
              btnPng={pngFile}
              setBtnPng={setPngFile}
              handleUploadClickSound={handleUploadClickSound}
              clickSoundFile={clickSoundFile}
              setClickSoundFile={setClickSoundFile}
              handleUploadPng={handleUploadPng}
              btnIcon={btnIcon}
              setBtnIcon={setBtnIcon}
              handleUploadIcon={handleUploadIcon}
              uploadingStates={uploadingStates}
              item={item}
              setItem={setItem}
              handleSave={handleSave}
              handleSaveButtonText={handleSaveButtonText}
              buttonColor={buttonColor}
              setPhotoFile={setPhotoFile}
              setMediaLogoFile={setMediaLogoFile}
              setAvatarVideoFile={setAvatarVideoFile}
              setQrCodeFile={setQrCodeFile}
              setPngFile={setPngFile}
              setUpperLeftLogoFile={setUpperLeftLogoFile}
              setUpperRightLogoFile={setUpperRightLogoFile}
              setBottomLeftLogoFile={setBottomLeftLogoFile}
              setBottomRightLogoFile={setBottomRightLogoFile}
              currentLanguage={currentLanguage}
            />
          )}

          {activeTab === 1 && (
            <MainWayfinderMedia
              userId={userId}
              buttonColor={buttonColor}
              type="location"
              title="Location Media"
              uploadedUrls={item.uploadedUrls || {}}
              setUploadedUrls={setItem}
              handleToggleChangeMedia={handleToggleChangeMedia}
              uploadingStates={uploadingStates}
              setUploadingStates={setUploadingStates}
              handleUpload={handleUploadMedia}
              handleDeleteLogo={handleDeleteMedia}
              handleSave={handleSave}
              loading={loadingMedia}
              categoryData={item}
              setCategoryData={setItem}
              fetchCategoryData={fetchLocationData}
              showSaveBtn={true}
            />
          )}

          {activeTab === 2 && (
            <div
              className="menu-card2"
              style={{ padding: "20px", marginTop: "20px" }}
            >
              <WayfinderLocationSettings
                item={item}
                setItem={setItem}
                handleSave={handleSave}
                buttonColor={buttonColor}
                userId={userId}
                locationId={locationId}
                mapVideoFile={mapVideoFile}
                setMapVideoFile={setMapVideoFile}
                handleUploadMapVideo={handleUploadMapVideo}
                avatarVideoFile={avatarVideoFile}
                setAvatarVideoFile={setAvatarVideoFile}
                handleUploadAvatarVideo={handleUploadAvatarVideo}
                logoBeforeMapFile={logoBeforeMapFile}
                setLogoBeforeMapFile={setLogoBeforeMapFile}
                handleUploadLogoBeforeMap={handleUploadLogoBeforeMap}
                currentLanguage={currentLanguage}
              />
            </div>
          )}

          {activeTab === 3 && (
            <div
              className="menu-card2"
              style={{ padding: "20px", marginTop: "20px" }}
            >
              {/* <WayfinderAvatarSettings
                item={item}
                setItem={setItem}
                handleSave={handleSave}
                buttonColor={buttonColor}
                userId={userId}
              /> */}

              {/* Language Selector */}
              {/* <div style={{ marginTop: "20px", marginBottom: "20px" }}>
                <LanguageSelector
                  userId={userId}
                  onLanguageChange={onLanguageChange}
                  compact={true}
                  currentLanguage={currentLanguage}
                />
              </div> */}

              {/* Text Direction Manager */}
              <TextDirectionManager
                buttonColor={buttonColor}
                userId={userId}
                locationId={locationId}
                currentTextDirections={item.textDirectionsMultiLang[currentLanguage]}
                onTextDirectionsUpdate={onTextDirectionsUpdate}
                spaceName={item.spaceName} // Pass the selected space name

                currentLanguage={currentLanguage}
                videoStatus={videoStatus}
                setVideoStatus={setVideoStatus}

              />

              {/* Video Progress for Current Language */}
              {currentLanguage && locationId && (
                <LocationVideoProgress
                  userId={userId}
                  locationId={locationId}
                  currentLanguage={currentLanguage}

                  videoStatus={videoStatus}
                  setVideoStatus={setVideoStatus}
                />
              )}
            </div>
          )}

          {activeTab === 4 && (
            <div
              className="menu-card2"
              style={{ padding: "20px", marginTop: "20px" }}
            >
              <GlobalAvatarSettings userId={userId} buttonColor={buttonColor} />
            </div>
          )}

          {activeTab === 5 && (
            <div
              className="menu-card2"
              style={{ padding: "20px", marginTop: "20px" }}
            >
              <MobileScreenTextEditor
                userId={userId}
                locationId={locationId}
                buttonColor={buttonColor}
              />
            </div>
          )}

          {/* Step-by-Step Directions Tab */}
          {activeTab === 6 && stepByStepConfig && (
            <div
              className="menu-card2"
              style={{ padding: "20px", marginTop: "20px" }}
            >
              <Box sx={{ width: "100%", mx: "auto" }}>
                <CardContent>
                  <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
                    Step-by-Step Directions Configuration
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Configure step-by-step voice directions for this location. 
                    You can use the global configuration, inherit from a parent category/subcategory, or customize it specifically for this location.
                  </Typography>

                  <StepByStepDirectionsConfig
                    config={stepByStepConfig}
                    onChange={setStepByStepConfig}
                    showUseParent={true}
                    parentOptions={parentOptions}
                    buttonColor={buttonColor}
                    disabled={false}
                  />

                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <button
                      style={{
                        backgroundColor: buttonColor,
                        color: "white",
                        border: "none",
                        padding: "10px 20px",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontWeight: "bold",
                      }}
                      type="button"
                      onClick={saveStepByStepConfig}
                    >
                      Save Step-by-Step Configuration
                    </button>
                  </Box>
                </CardContent>
              </Box>
            </div>
          )}

          {/* {activeTab === 6 && (
            <div
              className="menu-card2"
              style={{ padding: "20px", marginTop: "20px" }}
            >
              <MapPositionSettings
                item={item}
                setItem={setItem}
                handleSave={handleSave}
                buttonColor={buttonColor}
              />
            </div>
          )} */}
        </Box>
      </div>
    </div>
  );
}




export default WayfinderLocationTemplate;
